import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Dispatch, JSX, SetStateAction, useState, useEffect, useRef } from "react";
import { useForm, SubmitHandler, Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TAddPurchaseSchema, AddPurchaseSchema } from "../Models";
import { useAddPurchaseMutationHook, useRemovePurchaseMutationHook, useRebalancePurchaseRatiosMutationHook } from "@/hooks";
import { useFetchServicesOnTenderQueryHook } from "@/hooks/queries";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormattedNumberInputField, CustomToastComponent, InputField } from "@/components/shared";
import { CircleLoader } from "@/components/shared/loaders";
import ServiceRatioSelect from "../Components/ServiceRatioSelect";
import StepIndicator from "../Components/StepIndicator";
import { PurchaseDetails, ServiceRatioInput, ServiceType } from "@/graphql/__generated__/graphql";
import { convertDecimalToBp, convertBpToDecimal } from "@/utils/helpers";
import { toast } from "react-toastify";
import { Form } from "@/components/ui/form";

interface Props {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  purchaseDetails: PurchaseDetails | null;
}

/**
 * EditPurchaseDialog component for editing purchase details.
 * Uses delete-create pattern: removes old purchase and creates new one with updated values.
 *
 * Step 1: Edit purchase details
 * Step 2: Edit linked services
 *
 * @param {Props} props
 * @returns {JSX.Element}
 */
const EditPurchaseDialog = (props: Props): JSX.Element => {
  const { open, onOpenChange, purchaseDetails } = props;

  // Stepper state
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  // Hooks
  const { t } = useTranslation(["modals", "common", "translation", "toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  // Query hooks
  const { data: servicesData, loading: servicesLoading } = useFetchServicesOnTenderQueryHook(
    Number(tenderID),
    false,
    true
  );

  // Mutation hooks - disable toasts since we handle our own toast for edit operation
  const { addPurchaseMutation, loading: addPurchaseLoading } = useAddPurchaseMutationHook({
    disableToast: true,
  });
  const { removePurchaseMutation, loading: removePurchaseLoading } = useRemovePurchaseMutationHook({
    disableToast: true,
    disableRefetch: true,
  });
  const { rebalancePurchaseRatiosMutation, loading: rebalanceLoading } = useRebalancePurchaseRatiosMutationHook({
    disableToast: true,
  });

  // Store initial values for comparison
  const initialValuesRef = useRef<TAddPurchaseSchema | null>(null);

  // Get default values from existing purchase
  const getDefaultValues = (purchase: PurchaseDetails | null): TAddPurchaseSchema => {
    if (!purchase) {
      return {
        name: "",
        purchaseCost: 0,
        monthsDepreciation: 0,
        yearlyMaintenance: 0,
        yearlyRepair: 0,
        yearlyAdditionals: 0,
        quantity: 0,
        margin: 0,
      };
    }
    return {
      name: purchase.purchase.name,
      purchaseCost: purchase.purchase.purchaseCost,
      monthsDepreciation: purchase.purchase.monthsDepreciation,
      yearlyMaintenance: purchase.purchase.yearlyMaintenance,
      yearlyRepair: purchase.purchase.yearlyRepair,
      yearlyAdditionals: purchase.purchase.yearlyAdditionals,
      quantity: purchase.purchase.quantity,
      margin: convertBpToDecimal(purchase.purchase.margin),
    };
  };

  // Init form object
  const form = useForm<TAddPurchaseSchema>({
    resolver: zodResolver(AddPurchaseSchema) as Resolver<TAddPurchaseSchema>,
    mode: "onSubmit",
    defaultValues: getDefaultValues(purchaseDetails),
  });

  // State for service ratios
  const [selectedServices, setSelectedServices] = useState<ServiceRatioInput[]>([]);

  // Initialize form and service ratios when dialog opens or purchaseDetails changes
  useEffect(() => {
    if (open && purchaseDetails) {
      const defaults = getDefaultValues(purchaseDetails);
      form.reset(defaults);
      // Store initial values for comparison
      initialValuesRef.current = defaults;
      // Convert existing links from basis points to percentage for display
      if (purchaseDetails.links && purchaseDetails.links.length > 0) {
        const existingLinks = purchaseDetails.links.map((link) => ({
          serviceId: link.serviceId,
          ratio: convertBpToDecimal(link.ratio),
        }));
        setSelectedServices(existingLinks);
      } else {
        setSelectedServices([]);
      }
    }
  }, [open, purchaseDetails, form]);

  /**
   * Check if only service ratios have changed (purchase details remain the same)
   */
  const hasOnlyRatiosChanged = (newData: TAddPurchaseSchema): boolean => {
    if (!initialValuesRef.current) return false;

    const initial = initialValuesRef.current;
    return (
      initial.name === newData.name &&
      Number(initial.purchaseCost) === Number(newData.purchaseCost) &&
      Number(initial.monthsDepreciation) === Number(newData.monthsDepreciation) &&
      Number(initial.yearlyMaintenance) === Number(newData.yearlyMaintenance) &&
      Number(initial.yearlyRepair) === Number(newData.yearlyRepair) &&
      Number(initial.yearlyAdditionals) === Number(newData.yearlyAdditionals) &&
      Number(initial.quantity) === Number(newData.quantity) &&
      Number(initial.margin) === Number(newData.margin)
    );
  };

  // Get services for the dropdown (only permanent services, as mobile services cannot have purchases)
  const availableServices =
    servicesData?.fetchServicesOnTender?.services
      ?.filter((service) => service.type !== ServiceType.Mobile && service.additional === false)
      .map((service) => ({
        id: service.id,
        name: service.name,
        operationalOrganizationShortName: service.operationalOrganizationShortName,
      })) ?? [];

  // Calculate if service ratios are valid (either empty or sum to 100)
  const totalRatio = selectedServices.reduce((sum, s) => sum + s.ratio, 0);
  const isRatioValid = selectedServices.length === 0 || totalRatio === 100;

  const inputFieldsData = [
    {
      name: "purchaseCost",
      label: t("addpurchase.inputs.purchasecost.label"),
      placeholder: t("addpurchase.inputs.purchasecost.placeholder"),
      style: "currency",
    },
    {
      name: "monthsDepreciation",
      label: t("addpurchase.inputs.monthsDepreciation.label"),
      placeholder: t("addpurchase.inputs.monthsDepreciation.placeholder"),
      isInteger: true,
    },
    {
      name: "yearlyMaintenance",
      label: t("addpurchase.inputs.yearlymaintenance.label"),
      placeholder: t("addpurchase.inputs.yearlymaintenance.placeholder"),
      style: "currency",
    },
    {
      name: "yearlyRepair",
      label: t("addpurchase.inputs.yearlyrepair.label"),
      placeholder: t("addpurchase.inputs.yearlyrepair.placeholder"),
      style: "currency",
    },
    {
      name: "yearlyAdditionals",
      label: t("addpurchase.inputs.yearlyadditionals.label"),
      placeholder: t("addpurchase.inputs.yearlyadditionals.placeholder"),
      style: "currency",
    },
    {
      name: "quantity",
      label: t("addpurchase.inputs.quantity.label"),
      placeholder: t("addpurchase.inputs.quantity.placeholder"),
      isInteger: true,
    },
    {
      name: "margin",
      label: t("addpurchase.inputs.margin.label"),
      placeholder: t("addpurchase.inputs.margin.placeholder"),
      additionalSuffix: "%",
    },
  ];

  /**
   * Handle navigation to next step
   */
  const handleNext = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (currentStep === 1) {
      // Validate step 1 (form fields)
      const isValid = await form.trigger();
      if (isValid) {
        setCurrentStep(2);
      }
    }
  };

  /**
   * Handle navigation to previous step
   */
  const handleBack = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  /**
   * Handler function for editing a purchase.
   * If only ratios changed, use rebalance mutation.
   * Otherwise, use delete-create pattern.
   * @param {TAddPurchaseSchema} data
   */
  const onSubmit: SubmitHandler<TAddPurchaseSchema> = async (data: TAddPurchaseSchema) => {
    if (!purchaseDetails || !isRatioValid) return;

    setIsProcessing(true);

    try {
      // Convert service ratios from percentage (0-100) to basis points (0-10000)
      const servicesInBp = selectedServices.map((s) => ({
        ...s,
        ratio: convertDecimalToBp(s.ratio),
      }));

      // Check if only ratios changed - if so, use rebalance mutation
      if (hasOnlyRatiosChanged(data)) {
        const result = await rebalancePurchaseRatiosMutation({
          variables: {
            tenderId: Number(tenderID),
            purchaseId: purchaseDetails.purchase.id,
            linkedServices: servicesInBp,
          },
        });

        if (!result.error) {
          toast(CustomToastComponent, {
            toastId: "purchase-edited-success",
            data: {
              title: t("toasts:toasts.purchaseedited"),
              type: "success",
            },
          });

          // Reset form and close dialog
          setSelectedServices([]);
          setCurrentStep(1);
          onOpenChange(false);
        }
      } else {
        // Purchase details changed - use delete-create pattern
        // Step 1: Remove the old purchase
        await removePurchaseMutation({
          variables: {
            tenderId: Number(tenderID),
            purchaseId: purchaseDetails.purchase.id,
          },
        });

        // Step 2: Create new purchase with updated values
        form.clearErrors();

        // Convert margin from percentage to basis points
        const inputData = {
          ...data,
          margin: convertDecimalToBp(data.margin),
        };

        const result = await addPurchaseMutation({
          variables: {
            tenderId: Number(tenderID),
            linkedServices: servicesInBp,
            input: inputData,
          },
        });

        if (!result.errors) {
          toast(CustomToastComponent, {
            toastId: "purchase-edited-success",
            data: {
              title: t("toasts:toasts.purchaseedited"),
              type: "success",
            },
          });

          // Reset form and close dialog
          setSelectedServices([]);
          setCurrentStep(1);
          onOpenChange(false);
        }
      }
    } catch (error) {
      toast(CustomToastComponent, {
        toastId: "purchase-edit-error",
        data: {
          title: t("toasts:toasts.somethingwentwrong"),
          content: (error as Error).message || t("toasts:toasts.pleasetryagain"),
          type: "error",
        },
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedServices([]);
      setCurrentStep(1);
      form.reset(getDefaultValues(null));
    }
    onOpenChange(newOpen);
  };

  const isLoading = isProcessing || addPurchaseLoading || removePurchaseLoading || rebalanceLoading;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[90vw] lg:max-w-2xl overflow-y-auto max-h-[90vh] h-full md:h-auto">
        <Form {...form}>
          <form className="space-y-6" id="edit-purchase-form" onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>{t("editpurchase.title")}</DialogTitle>
              <DialogDescription>{t("editpurchase.fulldescription")}</DialogDescription>
            </DialogHeader>

            {/* Step Indicator */}
            <StepIndicator currentStep={currentStep} totalSteps={2} />

            {/* Step 1: Purchase Details */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">
                  {t("translation:stepper.step1description")}
                </h3>
                <div className="flex-wrap-4">
                  <div className="form-container">
                    <InputField
                      form={form}
                      name="name"
                      label={t("addpurchase.inputs.name.label")}
                      placeholder={t("addpurchase.inputs.name.placeholder")}
                      className="w-full"
                    />
                  </div>
                  {inputFieldsData.map((fieldData) => (
                    <div key={fieldData.name} className="form-container">
                      <FormattedNumberInputField
                        name={fieldData.name}
                        control={form.control}
                        additionalSuffix={fieldData.additionalSuffix}
                        placeholder={fieldData.placeholder}
                        style={fieldData.style as "currency" | "decimal"}
                        isInteger={fieldData.isInteger}
                        isStandalone={true}
                        label={fieldData.label}
                        className="w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Link to Services */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">
                  {t("translation:stepper.step2description")}
                </h3>
                <ServiceRatioSelect
                  services={availableServices}
                  selectedServices={selectedServices}
                  onServicesChange={setSelectedServices}
                  disabled={isLoading}
                  loading={servicesLoading}
                />
              </div>
            )}

            {/* Navigation Buttons */}
            <DialogFooter className="flex justify-between sm:justify-between">
              <div className="flex gap-2">
                {currentStep > 1 && (
                  <Button type="button" variant="outline" onClick={handleBack} disabled={isLoading}>
                    {t("common:buttons.back")}
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isLoading}
                >
                  {t("common:buttons.cancel")}
                </Button>
                {currentStep < 2 ? (
                  <Button type="button" onClick={handleNext}>
                    {t("common:buttons.next")}
                  </Button>
                ) : (
                  <Button type="submit" disabled={isLoading || !isRatioValid} className="gap-2">
                    <CircleLoader visible={isLoading} height={16} width={16} strokeWidth={2} />
                    {t("common:buttons.save")}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditPurchaseDialog;
